import React from 'react';
import PropTypes from 'prop-types';
import { FaMapMarkerAlt, FaStar } from 'react-icons/fa';
import {
  formatClubAcreage,
  formatClubDistance,
  formatClubDues,
  formatClubOpenings,
  formatClubStandCount,
} from '../utils/findClubDisplayUtils';
import { FindClubCardActions } from './FindClubCardActions';
import './FindClubFeaturedCard.css';

function StatCell({ label, value, accent = false }) {
  if (!value) return null;
  return (
    <div className="find-club-featured__stat">
      <div className="find-club-featured__stat-label">{label}</div>
      <div className={`find-club-featured__stat-value${accent ? ' find-club-featured__stat-value--accent' : ''}`}>
        {value}
      </div>
    </div>
  );
}

export function FindClubFeaturedCard({ club, highlighted = false }) {
  const distance = formatClubDistance(club.distance_miles);
  const acreage = formatClubAcreage(club.acreage);
  const stands = formatClubStandCount(club.stand_count);
  const dues = formatClubDues(club.annual_dues);
  const openings = formatClubOpenings(club.openings, club.max_members, club.member_count);
  const statusLabel = club.is_accepting_members ? 'Accepting members' : 'Waitlist';

  return (
    <article
      className={`find-club-featured${highlighted ? ' find-club-featured--highlighted' : ''}`}
      data-testid={`find-club-featured-${club.id}`}
    >
      <div className="find-club-featured__hero">
        {club.logo_url ? (
          <img className="find-club-featured__hero-image" src={club.logo_url} alt="" />
        ) : (
          <div className="find-club-featured__hero-placeholder" aria-hidden />
        )}
        <div className="find-club-featured__hero-overlay" aria-hidden />
        <span className="find-club-featured__promoted-badge">
          <FaStar aria-hidden />
          Promoted
        </span>
        {distance ? <span className="find-club-featured__distance">{distance}</span> : null}
        <div className="find-club-featured__hero-copy">
          <h3 className="find-club-featured__title">{club.name}</h3>
          {club.location_label ? (
            <p className="find-club-featured__location">
              <FaMapMarkerAlt aria-hidden />
              {club.location_label}
            </p>
          ) : null}
        </div>
      </div>

      <div className="find-club-featured__body">
        <div className="find-club-featured__status-row">
          <span className={`find-club-featured__status${club.is_accepting_members ? ' find-club-featured__status--accepting' : ''}`}>
            {statusLabel}
          </span>
          {openings ? <span className="find-club-featured__openings">{openings}</span> : null}
        </div>

        {club.listing_description ? (
          <blockquote className="find-club-featured__quote">
            “{club.listing_description}”
          </blockquote>
        ) : null}

        {club.president_name ? (
          <p className="find-club-featured__president">— {club.president_name}, President</p>
        ) : null}

        <div className="find-club-featured__stats">
          <StatCell label="Acreage" value={acreage} />
          <StatCell label="Stands" value={stands} />
          <StatCell label="Dues/yr" value={dues} accent />
        </div>

        <FindClubCardActions club={club} layout="stacked" />
      </div>
    </article>
  );
}

FindClubFeaturedCard.propTypes = {
  club: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    listing_description: PropTypes.string,
    location_label: PropTypes.string,
    logo_url: PropTypes.string,
    distance_miles: PropTypes.number,
    acreage: PropTypes.number,
    stand_count: PropTypes.number,
    member_count: PropTypes.number,
    max_members: PropTypes.number,
    openings: PropTypes.number,
    annual_dues: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    is_accepting_members: PropTypes.bool,
    require_approval_to_join: PropTypes.bool,
    president_name: PropTypes.string,
    president_id: PropTypes.number,
  }).isRequired,
  highlighted: PropTypes.bool,
};
